import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import _get from 'lodash/get';
import _isNil from 'lodash/isNil';
import _omitBy from 'lodash/omitBy';
import {
  showVerificationModal,
  toggleVerificationLoading,
  setVerificationInfo,
  hideVerificationModal,
} from 'containers/Auth/actions';
import { makeSelectAuth, makeSelectProfile } from 'containers/Auth/selectors';
import { post } from 'utils/api';
import Config from 'utils/getEnvConfig';
import useContentfulLocale from 'components/useContentfulLocale';
import { getBrandValue } from 'utils/brandUtils';
import useSiteCopySelector from 'components/useSiteCopySelector';
import {
  calculateRemainingMinutes,
  formatErrorMessage,
} from '../VerificationCodeDialog/utils';

const {
  LAMBDA: {
    FIREBASE_HELPER: { REQUEST_EMAIL_VERIFICATION_URL, API_KEY },
  },
} = Config;

const EMAIL_VERIFICATION_API_OPTIONS = {
  headers: {
    'x-api-key': API_KEY,
  },
};

/**
 * Hook for managing personal email verification flow
 * Handles API request, modal display, and success/error callbacks
 *
 * @param {Object} options
 * @param {string} options.personalEmail - The personal email to verify
 * @param {Function} options.onSuccess - Callback when verification succeeds
 * @param {Function} options.onError - Optional callback when verification fails
 * @returns {Object} { requestVerification }
 */
const usePersonalEmailVerification = ({
  personalEmail,
  onSuccess,
  onError,
}) => {
  const dispatch = useDispatch();
  const auth = useSelector(makeSelectAuth());
  const profile = useSelector(makeSelectProfile());
  const { locale } = useContentfulLocale();
  const [accountSiteCopy, verifyEmailSiteCopy] = useSiteCopySelector([
    'account',
    'verify-email',
  ]);

  // Check if this is initial personal email verification (no verified personal email yet)
  const isInitialPersonalEmailVerification = !profile?.personalEmailVerified;

  const handleVerificationSuccess = async () => {
    try {
      toast(_get(accountSiteCopy, 'pageCopy.personalEmailVerifySuccess'), {
        type: 'success',
      });

      // Call custom success handler if provided
      if (onSuccess) {
        await onSuccess();
      }

      dispatch(hideVerificationModal());
    } catch (error) {
      console.error('Error after personal email verification:', error);

      if (onError) {
        onError(error);
      }
    }
  };

  const requestVerification = async () => {
    try {
      dispatch(toggleVerificationLoading(true));

      const data = {
        brand: getBrandValue(),
        locale,
        isGuest: false,
        account: 'personal', // Backend uses this to distinguish personal from work
        token: _get(auth, 'stsTokenManager.accessToken'),
      };

      // For initial personal email verification, use 'email' field
      // For email change (already has verified personal email), use 'newEmail' with EMAIL_CHANGE type
      if (isInitialPersonalEmailVerification) {
        data.email = personalEmail;
      } else {
        data.newEmail = personalEmail;
        data.type = 'EMAIL_CHANGE';
        data.isEmailChange = true;
      }

      const response = await post(
        REQUEST_EMAIL_VERIFICATION_URL,
        _omitBy(data, _isNil),
        EMAIL_VERIFICATION_API_OPTIONS,
      );

      const responseData = _get(response, 'data');
      dispatch(
        setVerificationInfo({
          ...responseData,
          type: data.type,
          account: data.account,
        }),
      );

      // Show verification modal
      dispatch(
        showVerificationModal({
          show: true,
          redirect: null,
          payload: {
            email: personalEmail,
            isGuest: false,
          },
          info: {
            type: isInitialPersonalEmailVerification
              ? undefined
              : 'EMAIL_CHANGE',
            account: 'personal',
            callback: handleVerificationSuccess,
            showSuccessModal: false,
          },
        }),
      );

      return { success: true };
    } catch (requestError) {
      const errorType = _get(requestError, 'type');
      const errorLabels = _get(
        verifyEmailSiteCopy,
        ['pageCopy', 'verificationCodeLabels', 'errors'],
        {},
      );

      const errorMessage = _get(errorLabels, errorType);

      const displayError = formatErrorMessage(
        errorType,
        {
          remainingSeconds: requestError?.remainingSeconds,
          remainingMinutes:
            requestError?.remainingMinutes ||
            calculateRemainingMinutes(requestError?.lockedUntil),
          error: errorMessage || requestError?.error,
        },
        { errorLabels, errorText: 'Error occurred' },
      );

      toast(displayError, { type: 'error' });

      if (onError) {
        onError(requestError);
      }

      return { success: false, error: requestError };
    } finally {
      dispatch(toggleVerificationLoading(false));
    }
  };

  return {
    requestVerification,
  };
};

export default usePersonalEmailVerification;
